import { gql, DocumentNode } from '@apollo/client';
import { Box, List, Paper, Typography } from '@mui/material';
import React from 'react';
import { Button } from '../../Button';
import {
	canAddPaymentInstallment,
	paymentInstallmentIsMutable,
} from './helpers';
import { InvoiceEditPaymentInstallment } from './InvoiceEditPaymentInstallment';
import styles from './InvoiceEditPaymentInstallments.module.scss';
import { InvoiceEditPaymentInstallmentsHeader } from './InvoiceEditPaymentInstallmentsHeader';
import { UsePaymentInstallments } from '../usePaymentInstallments';
import { InvoiceEditPaymentInstallmentsInvoiceFragment } from '#types';
import { InvoiceEditRemainingBalance } from './InvoiceEditRemainingBalance';
import {
	InvoiceEditPaymentScheduleSelect,
	InvoiceEditPaymentScheduleSelectProps,
} from './InvoiceEditPaymentScheduleSelect';
import { useDeviceDetection } from '../../../hooks/useDeviceDetection';
import rpcShared from '@rockpapercoin/rpc-shared';

const invoiceEditPaymentInstallmentsInvoiceFragment = gql`
	fragment invoiceEditPaymentInstallmentsInvoice on Invoice {
		id
		status
		organization {
			country
		}
		...invoiceEditRemainingBalanceInvoice
		...invoiceEditPaymentInstallmentInvoice
		paymentInstallments {
			id
			dueDate
		}
	}
	${ InvoiceEditRemainingBalance.fragments
		.invoiceEditRemainingBalanceInvoiceFragment }
	${ InvoiceEditPaymentInstallment.fragments
		.invoiceEditPaymentInstallmentInvoiceFragment }
`;

type InvoiceEditPaymentInstallmentsProps = Pick<
UsePaymentInstallments,
| 'paymentInstallmentsDetails'
| 'onAddPaymentInstallments'
| 'onRemovePaymentInstallment'
| 'onUpdatePaymentInstallment'
| 'onBlur'
> & {
	invoiceExists: boolean;
} & Pick<InvoiceEditPaymentScheduleSelectProps, 'errorText'> & {
	invoice?: Omit<InvoiceEditPaymentInstallmentsInvoiceFragment, 'id'> & {
		id?: string;
	};
};

const sortByOptionalDueDate = (
	a: InvoiceEditPaymentInstallmentsInvoiceFragment['paymentInstallments'][number],
	b: InvoiceEditPaymentInstallmentsInvoiceFragment['paymentInstallments'][number]
) =>
	!a.dueDate && !b.dueDate
		? 0
		: !a.dueDate
			? 1
			: !b.dueDate
				? -1
				: new Date( a.dueDate ).getTime() - new Date( b.dueDate ).getTime();

type InvoiceEditPaymentInstallmentsPropsWithFragment =
	React.FC<InvoiceEditPaymentInstallmentsProps> & {
		fragments: {
			invoiceEditPaymentInstallmentsInvoiceFragment: DocumentNode;
		};
	};

export const InvoiceEditPaymentInstallments: InvoiceEditPaymentInstallmentsPropsWithFragment =
	( {
		invoice,
		invoiceExists,
		onAddPaymentInstallments,
		onRemovePaymentInstallment,
		onUpdatePaymentInstallment,
		onBlur,
		errorText,
		paymentInstallmentsDetails,
	} ) => {
		const { isMobile } = useDeviceDetection();

		const currencyLabel = rpcShared.invoices.getCurrencyLabel(
			invoice?.organization?.country
		);
		const amountLabel = `Amount${ currencyLabel }`;

		const somePaymentInstallmentsMutable =
			invoice?.paymentInstallments?.some( paymentInstallmentIsMutable ) || false;

		return (
			<Paper
				className={ styles.container }
				data-cy='invoice-edit-payment-installments'
			>
				<Typography variant='h6'>Payment schedule</Typography>
				{ !invoice?.paymentInstallments ||
				invoice?.paymentInstallments.length === 0 ? (
						<>
							<InvoiceEditPaymentScheduleSelect
								onChange={ ( e ) =>
									onAddPaymentInstallments(
										typeof e.target.value === 'string'
											? parseInt( e.target.value, 10 )
											: typeof e.target.value === 'number'
												? e.target.value
												: undefined
									)
								}
								errorText={ errorText }
							/>
							<Box className={ styles.bottomBarNoInstallments }>
								<Box />
								<InvoiceEditRemainingBalance invoice={ invoice } />
							</Box>
						</>
					) : (
						<>
							<InvoiceEditPaymentInstallmentsHeader
								isMobile={ isMobile }
								somePaymentInstallmentsMutable={ somePaymentInstallmentsMutable }
								amountLabel={ amountLabel }
							/>
							<List disablePadding>
								{ [ ...( invoice?.paymentInstallments || [] ) ]
									?.sort( sortByOptionalDueDate )
									?.map( ( paymentInstallment, index ) => (
										<InvoiceEditPaymentInstallment
											invoice={ invoice }
											key={ paymentInstallment.id }
											paymentInstallment={ paymentInstallment }
											paymentInstallmentDetails={ paymentInstallmentsDetails }
											onUpdate={ onUpdatePaymentInstallment( paymentInstallment.id ) }
											onRemove={ onRemovePaymentInstallment }
											onBlur={ onBlur }
											newInstallmentName={ `Installment ${ index + 1 }` }
											somePaymentInstallmentsMutable={
												somePaymentInstallmentsMutable
											}
											amountLabel={ amountLabel }
										/>
									) ) }
							</List>
							<Box className={ styles.bottomBar }>
								{ canAddPaymentInstallment( invoiceExists, invoice?.status ) ? (
									<Button
										className={ styles.addItemButton }
										variant='text'
										onClick={ () => onAddPaymentInstallments() }
										data-cy='invoice-edit-payment-installment-add'
									>
									Add Installment
									</Button>
								) : (
									<Box />
								) }
								<InvoiceEditRemainingBalance invoice={ invoice } />
							</Box>
						</>
					) }
			</Paper>
		);
	};

InvoiceEditPaymentInstallments.fragments = {
	invoiceEditPaymentInstallmentsInvoiceFragment,
};
